import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { DonationFormData, CountryOption, StateOption } from '../../types/donation';
import { donationService } from '../../services/api';
import './DonationForm.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// Luhn algorithm check for credit card numbers
const luhnCheck = (num: string): boolean => {
  const digits = (num || '').replace(/\D/g, '');
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = parseInt(digits.charAt(i), 10);
    if (Number.isNaN(d)) return false;
    if (shouldDouble) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    shouldDouble = !shouldDouble;
  }
  return digits.length > 0 && sum % 10 === 0;
};

interface DonationFormProps {
  entity?: string;
  emailAppName?: string;
  emailTemplateName?: string;
  title?: string;
}

const DonationForm: React.FC<DonationFormProps> = ({
  entity = 'RMS',
  emailAppName = 'Donation',
  emailTemplateName = 'RMSDonation',
  title,
}) => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [donationTypes, setDonationTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionStatus, setSubmissionStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [defaultCountryId, setDefaultCountryId] = useState<string>('');
  const [defaultStateId, setDefaultStateId] = useState<string>('');
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  // Brand-specific assets for overlays and visual cues (local SVG)
  const DEFAULT_LOGO = '';
  const RMS_LOGO = 'https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png';
  const HYC_LOGO = 'https://harmonyyouthcenter.org/Content/images/footer.png';
  const brandLogoSrc = entity === 'HYC' ? HYC_LOGO : (entity === 'RMS' ? RMS_LOGO : DEFAULT_LOGO);
  const brandAlt = entity === 'HYC' ? 'Harmony Youth Center' : 'Radha Madhav Society';

  if (!RECAPTCHA_SITE_KEY && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('RECAPTCHA_SITE_KEY is not set. Define REACT_APP_RECAPTCHA_SITE_KEY in your .env file.');
  }

  // Load and render Google reCAPTCHA v2 (checkbox) widget
  useEffect(() => {
    const ensureScript = (): Promise<void> => {
      return new Promise((resolve) => {
        const w = window as any;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window as any;
      const el = document.getElementById('recaptcha-container');
      if (!el) return;
      if (recaptchaWidgetId !== null) return; // already rendered

      // Always use standard grecaptcha for v2 checkbox
      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          // Script not yet attached to window
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            // Not ready yet; try again shortly
            setTimeout(doRender, 150);
            return;
          }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            // In case of intermittent readiness issues
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('reCAPTCHA render failed, retrying...', e);
            }
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          // Fallback if ready() is not available
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      // Defer a tick to ensure the container is mounted in DOM
      setTimeout(renderWidget, 0);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  // Date helpers for recurring donations
  const formatDate = (d: Date) => d.toISOString().slice(0, 10);
  const today = new Date();
  const todayStr = formatDate(today);
  const oneYearLaterInit = new Date(today);
  oneYearLaterInit.setFullYear(today.getFullYear() + 1);
  const oneYearLaterStr = formatDate(oneYearLaterInit);
  const formatMMDDYYYY = (iso: string) => {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    return `${m}/${d}/${y}`;
  };

  // Initial form values
  const initialValues: DonationFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNo: '',
    address: '',
    country: defaultCountryId || '',
    state: defaultStateId || '',
    city: '',
    postalCode: '',
    purpose: '',
    paymentPurpose: '',
    donationAmount: '',
    donationType: 'OneTime',
    paymentMethod: 'CreditCard',
    cardNumber: '',
    cvv: '',
    expirationDate: '',
    recurringStartDate: todayStr,
    recurringEndDate: oneYearLaterStr,
    // ACH defaults
    achRoutingNumber: '',
    achAccountNumber: '',
    achBankName: '',
    achAccountType: 'Checking'
  };

  // Form validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().max(50, 'First Name must be at most 50 characters').required('First Name is required'),
    lastName: Yup.string().max(50, 'Last Name must be at most 50 characters').required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phoneNo: Yup.string()
      .required('Phone Number is required')
      .test('phone-digits', 'Enter a valid phone number (10 to 15 digits)', (val: string | undefined) => {
        const digits = (val || '').replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
      }),
    address: Yup.string().max(60, 'Address must be at most 60 characters').required('Address is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().max(40, 'City must be at most 40 characters').required('City is required'),
    postalCode: Yup.string()
      .matches(/^\d{5}(?:-\d{4})?$/, 'Invalid US ZIP code')
      .required('Postal Code/ZIP is required'),
    // Comments (optional)
    purpose: Yup.string(),
    paymentPurpose: Yup.string().required('Payment Purpose is required'),
    donationAmount: Yup.string()
      .matches(/^\d{1,7}(?:\.\d{1,2})?$/, 'Donation must be a decimal number up to $1,000,000.00')
      .test('amount-range', 'Donation must be a decimal number up to $1,000,000.00', (val) => {
        if (!val) return false;
        const num = Number(val);
        if (!Number.isFinite(num)) return false;
        // strictly greater than 0 and not exceeding 1,000,000.00
        return num > 0 && num <= 1000000;
      })
      .required('Donation Amount is required'),
    donationType: Yup.string().required('Donation Type is required'),
    // Conditional card validations
    cardNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('Card Number is required')
            .test(
              'card-luhn-and-length',
              'Enter a valid card number (13–19 digits)',
              (val: string) => {
                const digits = (val || '').replace(/\D/g, '');
                if (!/^\d{13,19}$/.test(digits)) return false;
                return luhnCheck(digits);
              }
            )
        : schema.notRequired()
    ),
    cvv: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('CVV/CVC is required')
            .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits')
        : schema.notRequired()
    ),
    expirationDate: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('Expiration Date is required')
            .matches(/^(0[1-9]|1[0-2])\/(20[2-9][0-9])$/, 'Format must be MM/YYYY')
            .test('exp-not-past', 'Expiration must be a future month', (val: string) => {
              if (!val) return false;
              const match = val.match(/^(0[1-9]|1[0-2])\/(20[2-9][0-9])$/);
              if (!match) return false;
              const month = parseInt(match[1], 10);
              const year = parseInt(match[2], 10);
              const now = new Date();
              const currentMonth = now.getMonth() + 1; // 1-12
              const currentYear = now.getFullYear();
              return year > currentYear || (year === currentYear && month > currentMonth);
            })
        : schema.notRequired()
    ),

    // Recurring date validations
    recurringStartDate: Yup.string().when('donationType', (donType: any, schema: any) =>
      donType === 'Recurring'
        ? schema
            .required('Start Date is required')
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
            .test('start-not-past', 'Start Date cannot be in the past', (val: string) => {
              if (!val) return false;
              return val >= todayStr;
            })
        : schema.notRequired()
    ),
    recurringEndDate: Yup.string().when('donationType', function (donType: any, schema: any) {
      return donType === 'Recurring'
        ? schema
            .required('End Date is required')
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
            .test('end-after-year', 'End Date must be at least 1 year after Start Date', function (this: any, val: string) {
              const start = (this.parent as any)?.recurringStartDate as string;
              if (!val || !start) return false;
              const d = new Date(start + 'T00:00:00');
              const minEnd = new Date(d);
              minEnd.setFullYear(d.getFullYear() + 1);
              const minStr = minEnd.toISOString().slice(0, 10);
              return val >= minStr;
            })
        : schema.notRequired();
    }),

    // ACH validations (only when ACH selected)
    achRoutingNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema
            .required('Routing Number is required')
            .matches(/^\d{9}$/, 'Routing Number must be 9 digits')
        : schema.notRequired()
    ),
    achAccountNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema
            .required('Account Number is required')
            .matches(/^\d{4,17}$/, 'Account Number must be 4–17 digits')
        : schema.notRequired()
    ),
    achBankName: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH' ? schema.required('Bank Name is required') : schema.notRequired()
    ),
    achAccountType: Yup.mixed().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema.oneOf(['Checking', 'Savings']).required('Account Type is required')
        : schema.notRequired()
    ),
  });

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      const countryData = await donationService.getCountries();
      setCountries(countryData);
      // Determine USA as default if available
      const usa = countryData.find(c =>
        /^(united states|usa|united states of america)$/i.test(c.name)
      );
      const fallback = countryData[0];
      const selected = usa || fallback;
      if (selected) {
        setDefaultCountryId(selected.value);
        // Always fetch states by country ID (per requirement)
        const countryIdForStates = Number.parseInt(String(selected.value), 10) || Number(selected.id);
        const defaultStates = await donationService.getStatesByCountry(countryIdForStates);
        setStates(defaultStates);
        // Preselect Georgia if available
        const ga = defaultStates.find((s) => /^georgia$/i.test(s.name));
        if (ga) {
          setDefaultStateId(String(ga.id));
        } else if (defaultStates.length) {
          setDefaultStateId(String(defaultStates[0].id));
        }
      }
    };
    
    fetchCountries();
  }, []);

  // Fetch donation types for "This payment is for"
  useEffect(() => {
    const loadDonationTypes = async () => {
      const types = await donationService.getDonationTypes();
      setDonationTypes(types);
    };
    loadDonationTypes();
  }, []);

  // Fetch states when country changes
  const handleCountryChange = async (selectedCountry: any, formikProps: any) => {
    if (selectedCountry && selectedCountry.value) {
      formikProps.setFieldValue('country', selectedCountry.value);
      formikProps.setFieldValue('state', '');
      
      // Fetch states by selected country's ID
      const stateData = await donationService.getStatesByCountry(Number(selectedCountry.value));
      setStates(stateData);
      // Default to Georgia if present, else first state
      const ga = stateData.find((s) => /^georgia$/i.test(s.name));
      const chosen = ga || stateData[0];
      if (chosen) {
        formikProps.setFieldValue('state', String(chosen.id));
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: DonationFormData, formikHelpers?: any) => {
    setLoading(true);
    setSubmissionStatus(null);
    // Require a valid reCAPTCHA token before submitting
    if (!recaptchaToken) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'Please verify reCAPTCHA before submitting.' });
      return;
    }
    // Server-side verify the token
    const recaptchaResult = await donationService.verifyRecaptcha(recaptchaToken);
    if (!recaptchaResult || recaptchaResult.success !== true) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'reCAPTCHA verification failed. Please try again.' });
      try {
        const w = window as any;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch {}
      return;
    }
    
    try {
      // Helpers to map ids to labels for payload
      const countryLabel = countries.find(c => String(c.value) === String(values.country))?.label || '';
      const stateLabel = states.find(s => String(s.value) === String(values.state))?.label || '';
      const formatPhone = (p: string) => (p || '').replace(/\D/g, '');

      // Build MVC-like payload shape
      const description = `${entity} Donation for user: ${values.firstName} ${values.lastName} camp name: Camp amount  of : ${values.donationAmount}`;
      const appName = entity === 'HYC' ? 'flowery' : 'ykapps';
      const dataarray: any = {
        app: appName,
        entity: entity,
        paymentType: values.paymentMethod === 'ACH' ? 'ach' : 'cc',
        customerEmail: values.email,
        orderid: '0',
        amount: String(values.donationAmount),
        description,
        customer: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: formatPhone(values.phoneNo),
          company: 'string',
          notes: description,
          customerAccount: 'string',
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
          shippingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
        },
        creditcard: {
          creditCardNumber: values.cardNumber,
          expirationDate: values.expirationDate,
          issuer: 'string',
          billingZipCode: values.postalCode,
          customerId: 'string',
          cvv: values.cvv,
        },
        ach: {
          isCheckingAccount: String(values.achAccountType === 'Checking'),
          routingNumber: values.achRoutingNumber || 'string',
          accountNumber: values.achAccountNumber || 'string',
          bankName: values.achBankName || 'string',
          customerId: 'string',
          isDefault: 'true',
        },
        // Always include recurr block; will be populated for Recurring and kept as placeholders otherwise
        recurr: {
          paymentAmount: 'string',
          accountId: 'string',
          startDate: 'string',
          executionFrequencyType: 'string',
          executionFrequencyParameter: 'string',
          endDate: 'string',
          paymentSubType: 'string',
          invoiceNumber: 'string',
          firstPaymentAmount: 'string',
          firstPaymentDate: 'string',
          description: 'string',
        },
      };

      if (values.donationType === 'Recurring') {
        const now = new Date();
        const oneYearLater = new Date(now);
        oneYearLater.setFullYear(now.getFullYear() + 1);
        dataarray.recurr.paymentAmount = String(values.donationAmount);
        dataarray.recurr.accountId = 'string';
        dataarray.recurr.startDate = new Date(values.recurringStartDate + 'T00:00:00').toISOString();
        dataarray.recurr.executionFrequencyType = 'Monthly';
        dataarray.recurr.executionFrequencyParameter = '1';
        dataarray.recurr.endDate = new Date(values.recurringEndDate + 'T00:00:00').toISOString();
        dataarray.recurr.paymentSubType = 'string';
        dataarray.recurr.invoiceNumber = 'string';
        dataarray.recurr.firstPaymentAmount = String(values.donationAmount);
        dataarray.recurr.firstPaymentDate = now.toISOString();
        dataarray.recurr.description = description;
      }

      // Route to correct payment API with MVC-like payload
      const studentId: string = '0';
      const oneTimeFn = entity === 'HYC'
        ? donationService.authNetOneTimePaymentHYC
        : donationService.authNetOneTimePayment;
      const recurringFn = entity === 'HYC'
        ? donationService.authNetRecurringPaymentHYC
        : donationService.authNetRecurringPayment;
      const paymentResp: any = values.donationType === 'Recurring'
        ? await recurringFn(dataarray, studentId)
        : await oneTimeFn(dataarray);

      // Normalize success for both one-time and recurring responses
      const respMessages = (
        paymentResp?.messages ||
        paymentResp?.Messages ||
        paymentResp?.createTransactionResponse?.messages ||
        paymentResp?.message ||
        paymentResp?.Message ||
        {}
      );
      const resultCode = String(
        respMessages?.resultCode ?? paymentResp?.resultCode ?? ''
      );
      const msgArray = Array.isArray((respMessages as any)?.message)
        ? (respMessages as any).message
        : ((respMessages as any)?.message ? [(respMessages as any).message] : []);
      const messageCode = (msgArray[0]?.code) || (paymentResp?.createTransactionResponse?.messages?.message?.code);
      let messageText = '';
      if (typeof respMessages === 'string') {
        messageText = respMessages;
      } else {
        messageText = (msgArray[0]?.text || msgArray[0]?.description) || '';
      }
      // Fallbacks for top-level string messages
      if (!messageText && typeof (paymentResp as any)?.Message === 'string') {
        messageText = (paymentResp as any).Message;
      }
      if (!messageText && typeof (paymentResp as any)?.message === 'string') {
        messageText = (paymentResp as any).message;
      }
      const hasSubscriptionId = typeof paymentResp?.aRBCreateSubscriptionResponse?.subscriptionId === 'string' && paymentResp.aRBCreateSubscriptionResponse.subscriptionId.length > 0;
      const ok = (
        paymentResp?.success === true ||
        paymentResp?.isSuccess === true ||
        paymentResp?.status === 'OK' ||
        ['ok','OK','Ok'].includes(resultCode) ||
        messageCode === 'I00001' ||
        (values.donationType === 'Recurring' && hasSubscriptionId)
      );

      if (ok) {
        // Record donation in appropriate channel before sending email (non-blocking on failure)
        let addDonationStatus: any = null;
        try {
          const addDonationFn = entity === 'HYC'
            ? donationService.addHYCDonation
            : donationService.addDonation;
          const addResp: any = await addDonationFn({
            app: 'Donation',
            entity: entity,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNo: formatPhone(values.phoneNo),
            address: values.address,
            country: countryLabel,
            state: stateLabel,
            city: values.city,
            postalCode: values.postalCode,
            purposeOfDonation: values.purpose,
            paymentFor: values.paymentPurpose,
            donationAmount: Number(values.donationAmount || 0),
            donationType: values.donationType,
            paymentType: values.paymentMethod === 'ACH' ? 'ACH' : 'Credit Card',
          });
          const addMsg = (addResp?.message || addResp?.Message || 'Donation recorded successfully.');
          addDonationStatus = { success: true, message: addMsg };
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('AddDonation call failed (continuing to email):', (e as any)?.response?.data || e);
          }
          const data: any = (e as any)?.response?.data || (e as any)?.data || e;
          const addErrMsg = (data?.message || data?.Message || data?.error || (e as any)?.message || 'Unable to record donation.');
          addDonationStatus = { success: false, message: addErrMsg };
        }

        // Send confirmation email (non-blocking on failure)
        let emailStatus: any = null;
        try {
          const emailResp: any = await donationService.sendEmailMessage(
            {
              Email: values.email,
              ContactNo: values.phoneNo,
              UserName: `${values.firstName} ${values.lastName}`.trim(),
              DonationDate: new Date().toISOString(),
              Amount: String(values.donationAmount),
              Address: `${values.address || ''} ${values.city || ''} ${stateLabel || ''} ${countryLabel || ''}`.replace(/\s+/g, ' ').trim(),
            },
            { appName: emailAppName, templateName: emailTemplateName, orgId: 3 }
          );
          const emOk = emailResp?.success !== false; // treat as success unless explicitly false
          const emMsg = emailResp?.message || emailResp?.Message || (emOk ? 'A confirmation email was sent.' : 'Failed to send confirmation email.');
          let rurl: string = '';
          if (typeof emailResp === 'string') {
            rurl = emailResp; // API returned the PDF URL directly
          } else {
            rurl = (
              emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.receiptURL ||
              emailResp?.attachment || emailResp?.Attachment ||
              emailResp?.attachmentUrl || emailResp?.AttachmentUrl || emailResp?.attachmentURL ||
              emailResp?.pdfUrl || emailResp?.pdfURL ||
              emailResp?.fileUrl || emailResp?.fileURL ||
              emailResp?.url || emailResp?.Url || emailResp?.URL ||
              emailResp?.Response || emailResp?.response ||
              // nested under data
              emailResp?.data?.receiptUrl || emailResp?.data?.ReceiptUrl || emailResp?.data?.receiptURL ||
              emailResp?.data?.attachment || emailResp?.data?.Attachment ||
              emailResp?.data?.attachmentUrl || emailResp?.data?.AttachmentUrl || emailResp?.data?.attachmentURL ||
              emailResp?.data?.pdfUrl || emailResp?.data?.pdfURL ||
              emailResp?.data?.fileUrl || emailResp?.data?.fileURL ||
              emailResp?.data?.url || emailResp?.data?.Url || emailResp?.data?.URL ||
              emailResp?.data?.Response || emailResp?.data?.response ||
              // sometimes wrapped deeper
              emailResp?.Response?.attachmentUrl || emailResp?.Response?.AttachmentUrl || emailResp?.Response?.url ||
              emailResp?.response?.attachmentUrl || emailResp?.response?.AttachmentUrl || emailResp?.response?.url ||
              ''
            );
          }
          emailStatus = { success: emOk, message: emMsg, receiptUrl: rurl };
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Email send failed; continuing with success UI.', (e as any)?.response?.data || e);
          }
          const data: any = (e as any)?.response?.data || (e as any)?.data || e;
          const emMsg = (data?.message || data?.Message || (e as any)?.message || 'Failed to send confirmation email.');
          emailStatus = { success: false, message: emMsg };
        }

        // SweetAlert2 success popup
        if (typeof window !== 'undefined') {
          const extra = (values.donationType === 'Recurring' && hasSubscriptionId)
            ? ` Subscription ID: ${paymentResp.aRBCreateSubscriptionResponse.subscriptionId}.`
            : '';
          Swal.fire({
            icon: 'success',
            title: 'Thank you!',
            text: `Your donation was successful and a confirmation email has been sent.${extra}`,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Great',
            customClass: { popup: 'swal2-modern-popup' }
          });
        }

        const successMsg = (values.donationType === 'Recurring' && hasSubscriptionId)
          ? `Recurring donation successfully created. Subscription ID: ${paymentResp.aRBCreateSubscriptionResponse.subscriptionId}. Your first donation amount of $${values.donationAmount} will be charged on ${formatMMDDYYYY(values.recurringStartDate)} and will continue until ${formatMMDDYYYY(values.recurringEndDate)}.`
          : 'Donation successfully processed. Thank you!';
        setSubmissionStatus({ success: true, message: successMsg });
        // Reset the form to initial values
        if (formikHelpers && typeof formikHelpers.resetForm === 'function') {
          formikHelpers.resetForm();
        }
        // Reset reCAPTCHA after successful submission
        try {
          const w = window as any;
          if (recaptchaWidgetId !== null && w.grecaptcha) {
            w.grecaptcha.reset(recaptchaWidgetId);
            setRecaptchaToken('');
          }
        } catch {}

        // Persist grouped results and navigate to Success page
        try {
          if (typeof window !== 'undefined') {
            const donorName = `${values.firstName} ${values.lastName}`.trim();
            const flowResults = {
              originPath: window.location.pathname,
              donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
              payment: { success: true, message: successMsg },
              addDonation: addDonationStatus,
              email: emailStatus,
            } as any;
            window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
            window.location.href = '/success';
          }
        } catch {}
      } else {
        const errMsg = messageText || 'Failed to process donation. Please verify your details and try again.';
        setSubmissionStatus({ success: false, message: errMsg });

        // Persist failed payment and navigate to Success page
        try {
          if (typeof window !== 'undefined') {
            const donorName = `${values.firstName} ${values.lastName}`.trim();
            const flowResults = {
              originPath: window.location.pathname,
              donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
              payment: { success: false, message: errMsg },
              addDonation: null,
              email: null,
            } as any;
            window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
            window.location.href = '/success';
          }
        } catch {}
      }
    } catch (error: any) {
      // Try to surface a useful error message from API response (Axios error shapes)
      let apiMessage = 'An error occurred while processing your donation. Please try again later.';
      try {
        const data = error?.response?.data ?? error?.data ?? null;
        if (data) {
          // Common message containers
          const msgs = (data as any).messages
            || (data as any).Messages
            || (data as any).message
            || (data as any).Message
            || (data as any)?.createTransactionResponse?.messages
            || (data as any)?.aRBCreateSubscriptionResponse?.messages;

          // Meta.errors.errorMessages structure (e.g., Authorize.Net exception wrapper)
          try {
            const metaErrors = (data as any)?.meta?.errors?.errorMessages;
            if (Array.isArray(metaErrors) && metaErrors.length) {
              const first = metaErrors[0] || {};
              const metaMsg = first.message || first.description || first.text || first.error || '';
              if (metaMsg) {
                apiMessage = String(metaMsg);
              }
            }
          } catch {}

          if (typeof msgs === 'string') {
            apiMessage = msgs;
          } else if ((msgs as any)?.message || (msgs as any)?.Message) {
            const inner = (msgs as any).message ?? (msgs as any).Message;
            const arr = Array.isArray(inner) ? inner : [inner];
            const first = arr[0] || {};
            apiMessage = first.text || first.description || first.message || first.Message || apiMessage;
          }

          // Authorize.Net specific error block
          const anetErr = (data as any)?.createTransactionResponse?.errors?.error;
          if (!apiMessage && anetErr) {
            const arr = Array.isArray(anetErr) ? anetErr : [anetErr];
            apiMessage = arr[0]?.errorText || arr[0]?.text || apiMessage;
          }

          // ARB subscription specific messages
          const arbMsg = (data as any)?.aRBCreateSubscriptionResponse?.messages?.message;
          if (!apiMessage && arbMsg) {
            const arr = Array.isArray(arbMsg) ? arbMsg : [arbMsg];
            apiMessage = arr[0]?.text || arr[0]?.description || apiMessage;
          }

          // Generic errors array
          if (!apiMessage && Array.isArray((data as any)?.errors) && (data as any).errors.length) {
            const e0 = (data as any).errors[0];
            apiMessage = (e0 && (e0.message || e0.error || (typeof e0 === 'string' ? e0 : ''))) || apiMessage;
          }

          // Capitalized or lowercase top-level Message fields
          if (!apiMessage && typeof (data as any)?.Message === 'string') {
            apiMessage = (data as any).Message;
          }
          if (!apiMessage && typeof (data as any)?.message === 'string') {
            apiMessage = (data as any).message;
          }

          // Plain string response body
          if (!apiMessage && typeof data === 'string') {
            apiMessage = data;
          }
        } else if (typeof error?.message === 'string') {
          apiMessage = error.message;
        }
      } catch {}

      setSubmissionStatus({ success: false, message: apiMessage });
      // Persist fatal error and navigate to Success page
      try {
        if (typeof window !== 'undefined') {
          const donorName = `${values.firstName} ${values.lastName}`.trim();
          const flowResults = {
            originPath: window.location.pathname,
            donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
            payment: { success: false, message: apiMessage },
            addDonation: null,
            email: null,
          } as any;
          window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
          window.location.href = '/success';
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="donation-form-container">
      {/* Brand header with logo and styled title */}
      <div className="brand-header">
        {brandLogoSrc ? (
          <img src={brandLogoSrc} alt={brandAlt} className="brand-logo" />
        ) : null}
        <h1 className="brand-title">
          {entity === 'HYC' ? (title || 'Donation for Harmony Youth Center') : (title || 'Donation for Radha Madhav Society')}
        </h1>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <Form>
            <Row>
              {/* Personal Details Card */}
              <Col md={12}>
                <Card className="mb-4 elevated-card">
                  <Card.Body>
                    <div className="section-surface surface-personal">
                      {/* Personal Details in exactly 3 rows */}
                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="firstName">First Name *</label>
                        <Field name="firstName" type="text" className="form-control" placeholder="First Name" maxLength={50} />
                        <ErrorMessage name="firstName" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="lastName">Last Name *</label>
                        <Field name="lastName" type="text" className="form-control" placeholder="Last Name" maxLength={50} />
                        <ErrorMessage name="lastName" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="email">Email *</label>
                        <Field name="email" type="email" className="form-control" placeholder="Email" />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="phoneNo">Phone No *</label>
                        <Field name="phoneNo" type="text" className="form-control" placeholder="Phone No" />
                        <ErrorMessage name="phoneNo" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="address">Address *</label>
                        <Field name="address" as="textarea" className="form-control" placeholder="Address" maxLength={60} />
                        <ErrorMessage name="address" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="country">Select Country *</label>
                        <Select
                          options={countries.map(country => ({ value: country.value, label: country.label, id: country.id }))}
                          value={(formikProps.values.country && countries.length) ? { value: formikProps.values.country, label: countries.find(c => c.value === formikProps.values.country)?.label || '' } : null}
                          onChange={(option) => handleCountryChange(option, formikProps)}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Canada"
                        />
                        <ErrorMessage name="country" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="state">Select State *</label>
                        <Select
                          options={states.map(state => ({ value: state.value, label: state.label }))}
                          value={(formikProps.values.state && states.length) ? { value: formikProps.values.state, label: states.find(s => s.value === formikProps.values.state)?.label || '' } : null}
                          onChange={(option) => { if (option) { formikProps.setFieldValue('state', option.value); } }}
                          isDisabled={states.length === 0}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Select State"
                        />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="city">City *</label>
                        <Field name="city" type="text" className="form-control" placeholder="City" maxLength={40} />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="postalCode">Postal Code/ZIP Code *</label>
                        <Field name="postalCode" type="text" className="form-control" placeholder="Postal Code/ZIP Code" />
                        <ErrorMessage name="postalCode" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    </div>

                    <div className="section-surface surface-payment">
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="paymentPurpose">This payment is for *</label>
                        <Select
                          options={donationTypes}
                          value={formikProps.values.paymentPurpose ? { value: formikProps.values.paymentPurpose, label: formikProps.values.paymentPurpose } : null}
                          onChange={(option) => { if (option) { formikProps.setFieldValue('paymentPurpose', option.value); } }}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Search & Select Donation for"
                          isLoading={donationTypes.length === 0}
                        />
                        <ErrorMessage name="paymentPurpose" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="purpose">This payment for (comment)</label>
                        <Field name="purpose" type="text" className="form-control" placeholder="Add a short comment (optional)" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="donationAmount">Donation Amount *</label>
                        <Field name="donationAmount">
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              placeholder="Donation Amount"
                              onChange={(e) => {
                                let v = e.target.value;
                                v = v.replace(/[^0-9.]/g, '');
                                const parts = v.split('.');
                                if (parts.length > 2) { v = parts[0] + '.' + parts.slice(1).join(''); }
                                const [intp, decp = ''] = v.split('.');
                                const dec2 = decp.slice(0, 2);
                                v = decp.length ? `${intp}.${dec2}` : intp;
                                formikProps.setFieldValue('donationAmount', v);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage name="donationAmount" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="donationType">Select Donation Type *</label>
                        <Select
                          options={[
                            { value: 'OneTime', label: 'OneTime' },
                            { value: 'Recurring', label: 'Recurring' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('donationType', option.value);
                            }
                          }}
                          value={{ value: formikProps.values.donationType, label: formikProps.values.donationType }}
                          className="basic-select theme-a"
                          classNamePrefix="select"
                        />
                        <ErrorMessage name="donationType" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="paymentMethod">Select Payment Method *</label>
                        <Select
                          options={[
                            { value: 'CreditCard', label: 'Credit Card' },
                            { value: 'ACH', label: 'ACH' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('paymentMethod', option.value);
                            }
                          }}
                          value={{
                            value: formikProps.values.paymentMethod,
                            label: formikProps.values.paymentMethod === 'ACH' ? 'ACH' : 'Credit Card',
                          }}
                          className="basic-select theme-b"
                          classNamePrefix="select"
                        />
                      </Col>
                    </Row>
                    {formikProps.values.donationType === 'Recurring' && (
                      <Row className="mb-3">
                        <Col md={6}>
                          <label htmlFor="recurringStartDate">Start Date *</label>
                          <Field name="recurringStartDate">
                            {({ field }: any) => (
                              <input
                                {...field}
                                type="date"
                                id="recurringStartDate"
                                className={`form-control date-highlight ${formikProps.touched.recurringStartDate && formikProps.errors.recurringStartDate ? 'is-invalid' : ''}`}
                                min={todayStr}
                                required
                                aria-invalid={Boolean(formikProps.touched.recurringStartDate && formikProps.errors.recurringStartDate)}
                                aria-describedby="recurringStartDate-error"
                                inputMode="none"
                                onKeyDown={(e) => {
                                  const allowed = ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Enter'];
                                  if (!allowed.includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  formikProps.setFieldValue('recurringStartDate', v);
                                  try {
                                    const d = new Date(v + 'T00:00:00');
                                    if (!isNaN(d.getTime())) {
                                      const end = new Date(d);
                                      end.setFullYear(d.getFullYear() + 1);
                                      const endStr = end.toISOString().slice(0, 10);
                                      formikProps.setFieldValue('recurringEndDate', endStr);
                                    }
                                  } catch {}
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="recurringStartDate" component="div" className="error-modern" id="recurringStartDate-error" />
                        </Col>
                        <Col md={6}>
                          <label htmlFor="recurringEndDate">End Date *</label>
                          <Field name="recurringEndDate">
                            {({ field }: any) => (
                              <input
                                {...field}
                                type="date"
                                id="recurringEndDate"
                                className={`form-control date-highlight ${formikProps.touched.recurringEndDate && formikProps.errors.recurringEndDate ? 'is-invalid' : ''}`}
                                min={(() => {
                                  const s = formikProps.values.recurringStartDate;
                                  if (!s) return oneYearLaterStr;
                                  const d = new Date(s + 'T00:00:00');
                                  d.setFullYear(d.getFullYear() + 1);
                                  return d.toISOString().slice(0, 10);
                                })()}
                                required
                                aria-invalid={Boolean(formikProps.touched.recurringEndDate && formikProps.errors.recurringEndDate)}
                                aria-describedby="recurringEndDate-error"
                                inputMode="none"
                                onKeyDown={(e) => {
                                  const allowed = ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Enter'];
                                  if (!allowed.includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => e.preventDefault()}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="recurringEndDate" component="div" className="error-modern" id="recurringEndDate-error" />
                        </Col>
                      </Row>
                    )}

                    {/* Credit Card fields */}
                    {formikProps.values.paymentMethod === 'CreditCard' && (
                      <>
                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="cardNumber">Card Number (13–19 digits) *</label>
                            <Field name="cardNumber">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter card number"
                                  inputMode="numeric"
                                  maxLength={19}
                                  onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
                                    formikProps.setFieldValue('cardNumber', digits);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="cardNumber" component="div" className="text-danger" />
                          </Col>
                        </Row>
                        
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="cvv">CVV/CVC *</label>
                            <Field 
                              name="cvv"
                              type="password"
                              className="form-control"
                              placeholder="CVV"
                              maxLength={4}
                            />
                            <ErrorMessage name="cvv" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="expirationDate">Expiration MM/YYYY *</label>
                            <Field name="expirationDate">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="text"
                                  className="form-control"
                                  placeholder="MM/YYYY"
                                  maxLength={7}
                                  onChange={(e) => {
                                    let v = e.target.value;
                                    // Keep only digits
                                    v = v.replace(/\D/g, '');
                                    // Limit to 6 digits MMYYYY
                                    if (v.length > 6) v = v.slice(0, 6);
                                    // Auto-insert slash after MM when at least 3 digits typed
                                    if (v.length >= 3) {
                                      v = v.slice(0, 2) + '/' + v.slice(2);
                                    }
                                    formikProps.setFieldValue('expirationDate', v);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="expirationDate" component="div" className="text-danger" />
                          </Col>
                        </Row>
                      </>
                    )}
                    
                    {/* ACH fields */}
                    {formikProps.values.paymentMethod === 'ACH' && (
                      <>                       
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="achRoutingNumber">Routing Number *</label>
                            <Field 
                              name="achRoutingNumber"
                              type="text"
                              className="form-control"
                              placeholder="Enter Routing Number"
                              inputMode="numeric"
                              maxLength={9}
                            />
                            <ErrorMessage name="achRoutingNumber" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="achAccountNumber">Account Number *</label>
                            <Field 
                              name="achAccountNumber"
                              type="text"
                              className="form-control"
                              placeholder="Enter Account Number"
                              inputMode="numeric"
                            />
                            <ErrorMessage name="achAccountNumber" component="div" className="text-danger" />
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="achBankName">Bank Name *</label>
                            <Field 
                              name="achBankName"
                              type="text"
                              className="form-control"
                              placeholder="Enter Bank Name"
                            />
                            <ErrorMessage name="achBankName" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="achAccountType">Account Type *</label>
                            <Field as="select" name="achAccountType" className="form-control">
                              <option value="Checking">Checking</option>
                              <option value="Savings">Savings</option>
                            </Field>
                            <ErrorMessage name="achAccountType" component="div" className="text-danger" />
                          </Col>
                        </Row>
                      </>
                    )}
                    
                    {/* Google reCAPTCHA + Submit on the same row */}
                    <Row className="align-items-center mb-3">
                      <Col md={8}>
                        <div id="recaptcha-container"></div>
                        {!recaptchaToken && (<div className="text-danger mt-2">Please complete reCAPTCHA</div>)}
                      </Col>
                      <Col md={4} className="text-end">
                        <Button type="submit" variant="success" disabled={loading || !recaptchaToken} className="btn-submit btn-lg shadow-sm">
                          {loading ? (
                            'Processing...'
                          ) : (
                            <>
                              <span className="btn-icon" aria-hidden="true">
                                <svg className="donate-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                                  <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.401 8.168L12 18.896 4.665 23.165l1.401-8.168L.132 9.21l8.2-1.192L12 .587z" />
                                </svg>
                              </span>
                              Donate
                            </>
                          )}
                        </Button>
                        {loading && (
                          <div className="mt-2 d-flex align-items-center justify-content-end">
                            <Spinner animation="border" role="status" size="sm" className="me-2"><span className="visually-hidden">Processing...</span></Spinner>
                            <span>Please wait...</span>
                          </div>
                        )}
                      </Col>
                    </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 24,
          }}
          aria-live="polite"
          aria-busy="true"
          role="alert"
        >
          <img
            src={brandLogoSrc}
            alt={brandAlt}
            style={{ width: 160, height: 'auto', marginBottom: 16, opacity: 0.9 }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_LOGO; }}
          />
          <Spinner
            animation="border"
            role="status"
            variant="success"
            style={{ width: '3rem', height: '3rem', marginBottom: 12 }}
          >
            <span className="visually-hidden">Processing...</span>
          </Spinner>
          <div style={{ fontSize: 16, color: '#333', marginBottom: 8 }}>{brandAlt}</div>
          <div style={{ fontSize: 18, color: '#0f5132', fontWeight: 600 }}>Processing your donation...</div>
          <div style={{ marginTop: 8, color: '#555' }}>Please do not close this window.</div>
        </div>
      )}
    </Container>
  );
};

export default DonationForm;
